import { DownloadIcon, XIcon } from 'lucide-react';
import { Button, Separator, Spinner } from '@monksflow/monks-ui';
import type { KbDocumentSelection } from '@/features/knowledge-bases/model';
import { useTranslation } from 'react-i18next';

interface KnowledgeBaseSelectionBarProps {
  selection: KbDocumentSelection;
  /** Total documents in the KB — used for the "select all across pages" label. */
  totalCount: number;
  onDownload: () => void;
  isDownloading?: boolean;
}

/**
 * Inline selection toolbar for the KB documents table. Rendered in the
 * documents header slot (overlaying the search + Add Documents toolbar) while a
 * selection is active: dismisses the selection, states what is selected, offers
 * the "select all across every page" escalation, and drives the bulk download.
 * The page owns the mount/unmount animation.
 */
export function KnowledgeBaseSelectionBar({
  selection,
  totalCount,
  onDownload,
  isDownloading = false,
}: KnowledgeBaseSelectionBarProps) {
  const { t } = useTranslation('knowledge');

  const {
    selectedCount,
    isAllAcrossPages,
    allPageSelected,
    pageSelectableCount,
    onSelectAllAcrossPages,
    clearSelection,
  } = selection;

  // The escalation only makes sense once the whole visible page is selected and
  // there is still something off-page left to catch: below that the user is
  // mid-selection, and at totalCount there is nothing left to select.
  const showSelectAllAcrossPages =
    !isAllAcrossPages && allPageSelected && selectedCount < totalCount;

  // Across-pages mode with exclusions no longer covers everything, so it falls
  // back to the plain count. The page-scoped label only applies when the page
  // *is* the whole selection: selection survives pagination, so a page can be
  // fully selected while earlier pages still hold rows.
  const isEveryDocument = isAllAcrossPages && selectedCount >= totalCount;
  const isExactlyThisPage =
    !isAllAcrossPages && allPageSelected && selectedCount === pageSelectableCount;

  const selectionLabel = isEveryDocument
    ? t('knowledgeBases.knowledgeBaseSelectionBar.allSelectedAcrossPages', {
        count: totalCount,
      })
    : isExactlyThisPage
      ? t('knowledgeBases.knowledgeBaseSelectionBar.allSelectedOnPage', {
          count: pageSelectableCount,
        })
      : t('knowledgeBases.knowledgeBaseSelectionBar.documentsSelected', {
          count: selectedCount,
        });

  return (
    <div
      data-testid="kb-selection-bar"
      className="flex h-11 w-full items-center justify-between gap-4 rounded-full border border-border-light bg-surface px-1"
    >
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          roundness="full"
          aria-label={t('knowledgeBases.knowledgeBaseSelectionBar.clearSelection')}
          className="size-8 p-0"
          onClick={clearSelection}
        >
          <XIcon className="size-4" />
        </Button>

        <span className="text-sm font-medium text-foreground">{selectionLabel}</span>

        {showSelectAllAcrossPages && (
          <>
            <Separator orientation="vertical" className="-mr-1 ml-2 h-4" />
            <Button
              variant="ghost"
              size="mini"
              className="font-medium text-primary"
              onClick={onSelectAllAcrossPages}
            >
              {t('knowledgeBases.knowledgeBaseSelectionBar.selectAllAcrossPages', {
                count: totalCount,
              })}
            </Button>
          </>
        )}
      </div>

      <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm" onClick={onDownload} disabled={isDownloading}>
          {isDownloading ? <Spinner /> : <DownloadIcon className="size-4" />}
          {t('knowledgeBases.knowledgeBaseSelectionBar.download')}
        </Button>
      </div>
    </div>
  );
}

KnowledgeBaseSelectionBar.displayName = 'KnowledgeBaseSelectionBar';
